import FilterDialogFooter from '../Components/FilterDialogFooter';
import React, { useState } from 'react';
import CustomDatatableSearch from '../Components/CustomDatatableSearch';
import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, TextField } from '@mui/material';
import useScreenSize from '../hooks/useScreenSize';
import { withStyles } from '@mui/styles';
import { CardContent } from '@mui/material';
import { isEmptyArray } from './other';
import useDebounce from '../hooks/useDebounce';
import { useLazyEffect } from '../hooks/useLazyEffect';

// use this for multiselect filters to prevent filters
//from expanding out of screen when multiple filters are selected
export const ControlledMultiSelectFilter = ({ filterList, onChange, index, column, options, label }) => {
  return (
    <FormControl variant="standard" style={{ width: '325px' }}>
      <InputLabel htmlFor="select-multiple-chip">{label}</InputLabel>
      <Select
        variant="standard"
        multiple
        value={filterList[index]}
        renderValue={(selected) => selected.join(', ')}
        onChange={(event) => {
          filterList[index] = event.target.value;
          onChange(filterList[index], index, column);
        }}
      >
        {options.map((item) => (
          <MenuItem key={item} value={item}>
            <Checkbox color="primary" checked={filterList[index].indexOf(item) > -1} />
            <ListItemText primary={item} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
// for single selection filters
export const ControlledSingleSelectFilter = ({ filterList, onChange, index, column, options = [], label = '' }) => {
  return (
    <FormControl variant="standard" style={{ width: '325px' }}>
      <InputLabel htmlFor="select-multiple-chip">{label}</InputLabel>
      <Select
        variant="standard"
        renderValue={(value) => {
          const selected = value[0];

          return selected === null ? 'All' : options?.find((option) => option.value === selected)?.label;
        }}
        onChange={(event) => {
          const value = event.target.value;

          filterList[index] = value === null ? [] : [value];

          onChange(filterList[index], index, column);
        }}
        value={isEmptyArray(filterList[index]) ? [null] : filterList[index]}
      >
        <MenuItem selected value={null}>
          All
        </MenuItem>
        {options?.map((option, index) => (
          <MenuItem key={index} value={option?.value || option}>
            {option?.label || option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
//for custom textfield filters
export const DatatableTextfieldFilter = ({ filterList, onChange, index, column, label, delay = 400 }) => {
  const [newValue, setNewValue] = useState([]);
  const debouncedNewValue = useDebounce(newValue, delay);

  useLazyEffect(() => {
    onChange(filterList[index], index, column);
  }, [debouncedNewValue]);

  return (
    <TextField
      variant="standard"
      label={label}
      value={filterList[index] || ''}
      onChange={(event) => {
        const value = event.target.value;

        filterList[index] = value ? [value] : [];

        setNewValue(value);
      }}
    />
  );
};

export const datatableFixedHeaderStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 100,
  background: 'var(--colors-data-table-head)',
};

const customFilterDialogFooter = (currentFilterList, applyNewFilters) => (
  <FilterDialogFooter onClick={applyNewFilters} />
);

export const getTableFilters = (columns, filterList) => {
  return filterList.map((filter, index) => ({ column: columns[index].name, filter })).filter((x) => x.filter.length);
};

/**
 * To create MuiDateTable options in one way and keep listings code in one style.
 * Good example: listings/Shipments
 */

export const besideFilterChipStyles = { position: 'absolute', right: 210, top: 10 };
export const ChipFiltersWrapper = ({ children }) => {
  const { width } = useScreenSize();
  return (
    <div
      style={{
        position: 'absolute',
        left: 20,
        top: 18,
        maxWidth: width - 830,
        overflowX: 'auto',
        overflowY: 'hidden',
      }}
    >
      {children}
    </div>
  );
};
export const CustomFilterChip = withStyles({
  root: {
    background: 'white',
    padding: '5px',
  },
})(CardContent);
export const DEFAULT_ROWS_PER_PAGE_OPTIONS = [20, 50, 100];
export class MuiDataTableOptionsBuilder {
  options = {
    //default
    serverSide: true,
    rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE_OPTIONS,
    searchPlaceholder: 'Search ...',
    selectableRows: 'none',
    selectToolbarPlacement: 'none',
    onRowSelectionChange: null,
    customToolbarSelect: null,
    //pagination
    pagination: false,
    onChangePage: null,
    onChangeRowsPerPage: null,
    //sorting
    sort: false,
    onColumnSortChange: null,
    //filters
    filter: false,
    confirmFilters: false,
    onFilterConfirm: null,
    onFilterChipClose: null,
    customFilterDialogFooter: null,
    //search
    search: false,
    onSearchChange: null,
    customSearchRender: null,
    isMultiSearch: false,
    searchOptions: null,
    //download
    download: false,
    onDownload: null,
    downloadOptions: null,
    //table data
    page: 0,
    rowsPerPage: 20,
    searchText: '',
    sortOrder: {},
    //other
    count: 0,
    enableNestedDataAccess: '',
    print: true,
    viewColumns: true,
    onViewColumnsChange: null,
  };

  addPagination(onChangePage, onChangeRowsPerPage) {
    this.options.pagination = true;
    this.options.onChangePage = onChangePage;
    this.options.onChangeRowsPerPage = onChangeRowsPerPage;
    return this;
  }

  addSorting(onColumnSortChange) {
    this.options.sort = true;
    this.options.onColumnSortChange = onColumnSortChange;
    return this;
  }

  addFilters(onFilterConfirm, onFilterChipClose) {
    if (!onFilterConfirm || !onFilterChipClose) {
      throw Error('You should provide onFilterConfirm and onFilterChipClose functions to enable filters.');
    }
    this.options.filter = true;
    this.options.confirmFilters = true;
    this.options.onFilterConfirm = onFilterConfirm;
    this.options.customFilterDialogFooter = customFilterDialogFooter;
    this.options.onFilterChipClose = onFilterChipClose;
    return this;
  }

  addSearch(onSearchChange, isMultiSearch = false, searchOptions = []) {
    this.options.search = true;
    this.options.onSearchChange = onSearchChange;
    this.options.isMultiSearch = isMultiSearch;
    this.options.searchOptions = searchOptions;
    // this.options.customSearchRender = debounceSearchRender(500);
    this.options.customSearchRender = (value) => {
      return (
        <CustomDatatableSearch
          onChange={this.options.onSearchChange}
          isMultiSearch={this.options.isMultiSearch}
          searchOptions={this.options.searchOptions}
          value={value}
        />
      );
    };
    this.options.searchAlwaysOpen = true;
    return this;
  }

  addDownload(onDownload, downloadOptions) {
    this.options.download = true;
    this.options.onDownload = onDownload;
    this.options.downloadOptions = downloadOptions;
    return this;
  }

  setTableData({ page, rowsPerPage, searchText, sortOrder }) {
    this.options.page = page;
    this.options.rowsPerPage = rowsPerPage;
    this.options.searchText = searchText;
    this.options.sortOrder = sortOrder;
    return this;
  }

  setCount(count) {
    this.options.count = count;
    return this;
  }

  setRowsPerPageOptions(rowsPerPageOptions) {
    this.options.rowsPerPageOptions = rowsPerPageOptions;
  }

  setRowsOptions(selectableRows, onRowSelectionChange, customToolbarSelect, selectToolbarPlacement) {
    this.options.selectableRows = selectableRows;
    this.options.onRowSelectionChange = onRowSelectionChange;
    this.options.customToolbarSelect = customToolbarSelect;
    this.options.selectToolbarPlacement = selectToolbarPlacement;
    return this;
  }

  addCustom(customOptions) {
    this.options = {
      ...this.options,
      ...customOptions,
    };

    return this;
  }

  enableNestedDataAccess(symbol = '.') {
    this.options.enableNestedDataAccess = symbol;
    return this;
  }

  removePrinting() {
    this.options.print = false;
    return this;
  }

  removeColumnsView() {
    this.options.viewColumns = false;
    return this;
  }

  addViewColumnsChange(onChange) {
    this.options.onViewColumnsChange = onChange;
    return this;
  }

  build() {
    return this.options;
  }
}
